import React, { useCallback, useState } from 'react';
import { View, KeyboardAvoidingView, Platform, Pressable, ScrollView } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';

import { APP_ID, AUTH_NAMES } from '../../../common/constants';
import { loginSucceeded } from '../../../common/components/Auth/authSlice';
import { LOGIN } from '../../../common/graphql/auth';
import { useForm } from '../../../common/utils/hooks';
import { getErrorMessage } from '../../../common/utils/auth';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Logo from '../logo/Logo';
import { TextInput } from '../input';
import Text from '../text/Text';
import { PrimaryButton } from '../../design/components';
import { Spinner } from '../spinner/Spinner';
import { ErrorCallout } from '../callout';
import { createAuthStyles } from './authStyles';


const AuthLogin = ({ navigation }) => {
	const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
	const dispatch = useDispatch();
	const styles = useThemedStyles(createAuthStyles);

	const [error, setError] = useState('');

	const [loginMutation, { loading }] = useMutation(LOGIN);

	const { values: { username, password }, onChange } = useForm();

	const login = async () => {
		// Strip whitespace on front and end of username and set to lower case before submitting
		const sanitisedUsername = username.trim().toLowerCase();

		let result = '';
		try {
			await AsyncStorage.removeItem('username');
			await AsyncStorage.removeItem('token');

			result = await loginMutation({
				variables: {
					input: {
						appId: APP_ID.MOBILE,
						username: sanitisedUsername,
						password,
					},
				},
			});
		}
		catch (responseError) {
			setError(responseError.graphQLErrors[0]);
		}

		if (result) {
			const token = result.data.authLogin.value;

			await AsyncStorage.setItem('username', sanitisedUsername);
			await AsyncStorage.setItem('token', token);

			dispatch(loginSucceeded());
		}
	};

	const goToResetPassword = useCallback(() => navigation.navigate('ResetPassword'), [navigation]);

	if (isAuthenticated) {
		// Navigation will be handled by AppNavigation conditionally
		return <Spinner />;
	}

	return (
		<KeyboardAvoidingView
			style={styles.container}
			behavior={Platform.OS === 'ios' ? 'padding' : undefined}
		>
			<ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
				<View style={styles.form}>
					<View style={styles.logoWrap}>
						<Logo />
					</View>
					<Text style={styles.subtitle}>Log in with your Sentry account</Text>
					<TextInput
						autoCapitalize="none"
						autoComplete="email"
						autoCorrect={false}
						keyboardType="email-address"
						onChange={onChange('username')}
						onKeyPress={() => setError('')}
						placeholder="Email"
						style={styles.input}
						textContentType="username"
						value={username}
					/>
					<TextInput
						autoComplete="current-password"
						onChange={onChange('password')}
						onKeyPress={() => setError('')}
						onSubmitEditing={login}
						password={true}
						placeholder="Password"
						returnKeyType="go"
						style={styles.input}
						textContentType="password"
						value={password}
					/>
					{
						Boolean(error) && <ErrorCallout text={getErrorMessage(error, AUTH_NAMES.LOGIN)} />
					}
					<PrimaryButton
						label="Log in"
						onPress={login}
						loading={loading}
						disabled={!username || !password}
						style={styles.button}
					/>
					<Pressable
						onPress={goToResetPassword}
						accessibilityRole="link"
						style={({ pressed }) => [styles.link, pressed && styles.linkPressed]}
					>
						<Text style={styles.linkText}>Forgot your password?</Text>
					</Pressable>
				</View>
			</ScrollView>
		</KeyboardAvoidingView>
	);
};

export default AuthLogin;
